import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import * as Tone from "tone";
import styled from "styled-components";
import { screenPanelStyles } from "../ScreenPanel";
import {
  SequencerHandle,
  SequencerProps,
  SequencerSnapshot,
  Step,
  PatternGeneration,
  ChordTiming,
  DEFAULT_PATTERN_GENERATION,
  DEFAULT_CHORD_TIMING,
  SequencerProgressionValue,
} from "./types";
import { useChordProgression } from "./hooks/useChordProgression";
import { usePatternManagement } from "./hooks/usePatternManagement";
import { useSequencerLogic } from "./hooks/useSequencerLogic";
import ProgressionSection from "./components/ProgressionSection";
import SequencerSteps from "./components/SequencerSteps";
import FXMappingView from "./components/FXMappingView";
import Selector from "./components/Selector";
import PatternGenerationControls from "./components/PatternGenerationControls";
import { PATTERN_PRESETS } from "../../utils/patternPresets";
import {
  getCurrentFilterCutoffHz,
  getCurrentReverbMix,
  getCurrentDelayMix,
  getReverseMix,
} from "../../utils/audioUtils";
import { getRomanChordNotes, playRomanInKey } from "../../utils/chordUtils";
import { expandDroneNotesToRegister } from "../../utils/droneNotes";
import { usePerformanceActions } from "../../context/PerformanceContext";

const FILTER_MIN = 20;
const FILTER_MAX = 10000;
const FILTER_RANGE = FILTER_MAX - FILTER_MIN;

const Sequencer = forwardRef<SequencerHandle, SequencerProps>(
  (
    {
      bpm = 120,
      stepsCount: initialStepsCount = 16,
      sampler = null,
      samplesMap = null,
      isKeyboardMode = false,
      onIsPlayingChange,
      onActiveNotesChange,
      onProgressionStateChange,
      disabled = false,
      mappingMode = null,
      fxModes,
      onMappingModeChange,
    },
    ref,
  ) => {
    const { getStrum } = usePerformanceActions();

    const [patternGeneration, setPatternGeneration] =
      React.useState<PatternGeneration>({
        ...DEFAULT_PATTERN_GENERATION,
        steps: Math.min(Math.max(initialStepsCount, 1), 16),
      });

    const stepsCount = patternGeneration.steps;
    const setStepsCount = useCallback((count: number) => {
      const clampedCount = Math.min(Math.max(count, 1), 16);
      setPatternGeneration((prev) => ({ ...prev, steps: clampedCount }));
    }, []);

    const {
      selectedKey,
      selectedMode,
      progression,
      chordMapping,
      filteredProgressions,
      handleModeChange,
      handleKeyChange,
      handleProgressionChange,
      handleChordUpdate,
      handleChordSwap,
      setSelectedKey,
      setProgressionFromValue,
      progressionName,
      register,
      setRegister,
      sequencerMode,
      droneNotes,
      handleSequencerModeChange,
      handleDroneNotesChange,
      ribbonPositions,
      setRibbonPositions,
      chordBars,
      setChordBars,
    } = useChordProgression();

    const {
      pattern,
      selectedPreset,
      toggleStep,
      applyPatternPreset,
      handlePresetChange,
      updateStepFXMix,
      initializeStepsWithGlobalValue,
      clearMapping,
      setPattern,
    } = usePatternManagement(stepsCount, sampler, sequencerMode);

    const chordTiming: ChordTiming = React.useMemo(() => {
      if (ribbonPositions && ribbonPositions.length > 0) {
        return {
          bars: chordBars,
          ribbonPositions: ribbonPositions,
        };
      }
      return {
        ...DEFAULT_CHORD_TIMING,
        bars: chordBars,
      };
    }, [ribbonPositions, chordBars]);

    const {
      currentStep,
      currentChordIndex,
      error,
      isPlaying,
      startSequencer,
      stopSequencer,
      prepareSequencer,
    } = useSequencerLogic(
      bpm,
      stepsCount,
      pattern,
      progression,
      selectedKey,
      sampler,
      samplesMap,
      isKeyboardMode,
      disabled,
      fxModes,
      register,
      sequencerMode,
      droneNotes,
      patternGeneration,
      chordTiming,
    );

    const displayTopNoteRef = useRef<number | undefined>(undefined);
    const previewTopNoteRef = useRef<number | undefined>(undefined);

    const handlePreviewChord = useCallback(
      async (roman: string) => {
        if (!sampler || disabled) return;

        try {
          await Tone.start();
        } catch (previewError) {
          console.error("Unable to start audio for chord preview:", previewError);
        }

        const topNote = playRomanInKey(sampler, {
          roman,
          key: selectedKey,
          mode: selectedMode,
          duration: "2n",
          velocity: 0.8,
          strum: getStrum(),
          register,
          previousTopNote: previewTopNoteRef.current,
        });

        if (topNote !== undefined) {
          previewTopNoteRef.current = topNote;
        }
      },
      [sampler, disabled, selectedKey, selectedMode, getStrum, register],
    );

    useEffect(() => {
      if (!onIsPlayingChange) {
        return;
      }
      onIsPlayingChange(isPlaying);
    }, [onIsPlayingChange, isPlaying]);

    useEffect(() => {
      if (!onActiveNotesChange) return;

      if (!isPlaying || currentStep < 0) {
        onActiveNotesChange([]);
        return;
      }

      const currentStepObj = pattern[currentStep];
      if (!currentStepObj || !currentStepObj.active) {
        onActiveNotesChange([]);
        return;
      }

      if (currentStepObj.mode === "drone") {
        onActiveNotesChange(
          expandDroneNotesToRegister(droneNotes ?? [], register),
        );
        return;
      }

      if (currentStepObj.mode === "chord") {
        if (currentChordIndex < 0) {
          onActiveNotesChange([]);
          return;
        }

        const roman = progression.roman[currentChordIndex];
        if (!roman) {
          onActiveNotesChange([]);
          return;
        }

        const result = getRomanChordNotes({
          roman,
          key: selectedKey,
          mode: progression.mode,
          register,
          previousTopNote: displayTopNoteRef.current,
        });

        if (result.topNote !== undefined) {
          displayTopNoteRef.current = result.topNote;
        }

        onActiveNotesChange(result.notes);
      }
    }, [
      onActiveNotesChange,
      isPlaying,
      currentStep,
      currentChordIndex,
      pattern,
      progression,
      selectedKey,
      register,
      droneNotes,
    ]);

    useEffect(() => {
      displayTopNoteRef.current = undefined;
    }, [progression, register]);

    useEffect(() => {
      previewTopNoteRef.current = undefined;
    }, [selectedKey, selectedMode, register]);

    const samplesReady = !disabled && !!sampler;

    const getCurrentGlobalValue = useCallback((): number => {
      if (!mappingMode) return 0;

      if (mappingMode === "filter") {
        const filterHz = getCurrentFilterCutoffHz();
        return Math.min(Math.max((filterHz - FILTER_MIN) / FILTER_RANGE, 0), 1);
      }
      if (mappingMode === "reverb") {
        return getCurrentReverbMix();
      }
      if (mappingMode === "delay") {
        return getCurrentDelayMix();
      }
      if (mappingMode === "reverse") {
        return getReverseMix();
      }

      return 0;
    }, [mappingMode]);

    const handleStepFXMixChange = useCallback(
      (stepIndex: number, fxType: string, value: number) => {
        updateStepFXMix(stepIndex, fxType, value);
      },
      [updateStepFXMix],
    );

    const hasMappingApplied = useCallback(
      (fxType: string): boolean => {
        return pattern.some((step) => {
          if (!step.fxMixes) return false;
          return step.fxMixes[fxType as keyof typeof step.fxMixes] !== undefined;
        });
      },
      [pattern],
    );

    const getProgressionState = useCallback(() => {
      return {
        name: progressionName,
        roman: [...progression.roman],
        mode: progression.mode,
      };
    }, [progressionName, progression]);

    const getSnapshot = useCallback((): SequencerSnapshot => {
      return {
        selectedKey,
        selectedProgression: progressionName || "custom",
        progressionState: getProgressionState(),
        patternPreset: selectedPreset,
        pattern: pattern.map((step) => ({
          ...step,
          ...(step.fxMixes ? { fxMixes: { ...step.fxMixes } } : {}),
        })) as Step[],
        stepsCount,
        ribbonPositions: ribbonPositions.map((position) => ({ ...position })),
        register: { ...register },
        sequencerMode,
        droneNotes: [...droneNotes],
      };
    }, [
      getProgressionState,
      selectedKey,
      progressionName,
      selectedPreset,
      pattern,
      stepsCount,
      ribbonPositions,
      register,
      sequencerMode,
      droneNotes,
    ]);

    useEffect(() => {
      if (onProgressionStateChange) {
        onProgressionStateChange(getProgressionState());
      }
    }, [onProgressionStateChange, getProgressionState]);

    useImperativeHandle(
      ref,
      (): SequencerHandle => ({
        prepare: prepareSequencer,
        start: startSequencer,
        stop: stopSequencer,
        setKey: (key: string) => {
          setSelectedKey(key);
        },
        setProgression: (value: SequencerProgressionValue) => {
          setProgressionFromValue(value);
        },
        applyPatternPreset: (presetKey: string) => {
          applyPatternPreset(presetKey);
        },
        setStepsCount: (count: number) => {
          setStepsCount(count);
        },
        setPattern: (nextPattern: Step[]) => {
          setPattern(nextPattern);
        },
        setRibbonPositions: (
          positions: Array<{ startPercent: number; endPercent: number }>,
        ) => {
          setRibbonPositions(positions);
        },
        setRegister: (nextRegister) => {
          setRegister(nextRegister);
        },
        setSequencerMode: (mode) => {
          handleSequencerModeChange(mode);
        },
        setDroneNotes: (notes: string[]) => {
          handleDroneNotesChange(notes);
        },
        initializeStepsWithGlobalValue: (fxType: string, globalValue: number) => {
          initializeStepsWithGlobalValue(fxType, globalValue);
        },
        hasMappingApplied: (fxType: string) => {
          return hasMappingApplied(fxType);
        },
        getSnapshot,
      }),
      [
        prepareSequencer,
        startSequencer,
        stopSequencer,
        setSelectedKey,
        setProgressionFromValue,
        applyPatternPreset,
        setStepsCount,
        setPattern,
        setRibbonPositions,
        setRegister,
        handleSequencerModeChange,
        handleDroneNotesChange,
        initializeStepsWithGlobalValue,
        hasMappingApplied,
        getSnapshot,
      ],
    );

    return (
      <SequencerContainer id="sequencer">
        {error && <ErrorContainer id="error">{error}</ErrorContainer>}

        <SequencerStepsWrapper id="sequencer-steps-wrapper">
          <SequencerStepsContent id="sequencer-steps-content">
            {!mappingMode && (
              <PatternSelectorBar id="pattern-selector-bar">
                <PatternGenerationControlsWrapper>
                  <PatternGenerationControls
                    id="pattern-generation-controls"
                    config={patternGeneration}
                    onChange={(newConfig) => {
                      const clampedConfig = {
                        ...newConfig,
                        steps: Math.min(Math.max(newConfig.steps, 1), 16),
                      };
                      setPatternGeneration(clampedConfig);
                    }}
                    disabled={!samplesReady}
                  />
                </PatternGenerationControlsWrapper>
                <PatternSelectorWrapper id="pattern-selector-wrapper">
                  <Selector
                    id="pattern-selector"
                    value={selectedPreset}
                    options={Object.entries(PATTERN_PRESETS).map(
                      ([key, preset]) => ({
                        key,
                        label: preset.name,
                        description: preset.description,
                      }),
                    )}
                    onChange={(value) => handlePresetChange(value)}
                    onPrevious={() => {
                      if (!samplesReady) return;
                      const presetKeys = Object.keys(PATTERN_PRESETS);
                      const currentIndex = presetKeys.indexOf(selectedPreset);
                      const prevIndex =
                        currentIndex > 0
                          ? currentIndex - 1
                          : presetKeys.length - 1;
                      handlePresetChange(presetKeys[prevIndex]);
                    }}
                    onNext={() => {
                      if (!samplesReady) return;
                      const presetKeys = Object.keys(PATTERN_PRESETS);
                      const currentIndex = presetKeys.indexOf(selectedPreset);
                      const nextIndex = (currentIndex + 1) % presetKeys.length;
                      handlePresetChange(presetKeys[nextIndex]);
                    }}
                    disabled={!samplesReady}
                  />
                </PatternSelectorWrapper>
              </PatternSelectorBar>
            )}

            {mappingMode ? (
              <FXMappingView
                pattern={pattern}
                currentStep={currentStep}
                progression={progression}
                chordMapping={chordMapping}
                mappingMode={mappingMode}
                onStepFXMixChange={handleStepFXMixChange}
                getCurrentGlobalValue={getCurrentGlobalValue}
                onMappingModeChange={onMappingModeChange}
                onClearMapping={clearMapping}
                stepsCount={stepsCount}
              />
            ) : (
              <SequencerSteps
                pattern={pattern}
                currentStep={currentStep}
                progression={progression}
                chordMapping={chordMapping}
                onToggleStep={toggleStep}
                mappingMode={mappingMode}
                stepsCount={stepsCount}
              />
            )}
          </SequencerStepsContent>
        </SequencerStepsWrapper>

        <ProgressionSection
          selectedKey={selectedKey}
          selectedMode={selectedMode}
          progression={progression}
          filteredProgressions={filteredProgressions}
          samplesReady={samplesReady}
          onKeyChange={handleKeyChange}
          onModeChange={handleModeChange}
          onProgressionChange={handleProgressionChange}
          onChordUpdate={handleChordUpdate}
          onChordSwap={handleChordSwap}
          sequencerMode={sequencerMode}
          droneNotes={droneNotes}
          onSequencerModeChange={handleSequencerModeChange}
          onDroneNotesChange={handleDroneNotesChange}
          ribbonPositions={ribbonPositions}
          onRibbonPositionsChange={setRibbonPositions}
          chordBars={chordBars}
          onChordBarsChange={setChordBars}
          currentChordIndex={currentChordIndex}
          onPreviewChord={handlePreviewChord}
        />
      </SequencerContainer>
    );
  },
);

Sequencer.displayName = "Sequencer";

export default Sequencer;

// Styled Components
const SequencerContainer = styled.div`
  border-radius: 8px;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ErrorContainer = styled.div`
  background-color: #ffeeee;
  color: #cc0000;
  padding: 10px;
  margin-bottom: 20px;
  border-radius: 4px;
  border: 1px solid #cc0000;
`;

const SequencerStepsWrapper = styled.div`
  ${screenPanelStyles}
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 160px;
  position: relative;
`;

const SequencerStepsContent = styled.div.attrs({
  id: "sequencer-steps-content-wrapper",
})`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PatternSelectorBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 8px 16px;
  margin-bottom: 0;
  position: relative;
`;

const PatternGenerationControlsWrapper = styled.div`
  grid-column: 1;
  justify-self: start;
`;

const PatternSelectorWrapper = styled.div`
  display: flex;
  justify-content: center;
  grid-column: 2;
  min-width: 200px;
`;
